/* eslint-disable max-len */
/* eslint-disable no-undef */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-region-selector.html";
import {
  GeoIPMarketRedirectV2,
  SitesLocalStorageService,
  SitesSessionStorageService
} from "@nuskin/ns-util";

import "@nuskin/ns-style-elements";

/**
 * The ns-region-selector should be used for
 *
 * <h1>region-selector</h1>
 * <demo-snippet>
 *   <template>
 *     <ns-region-selector class="demoElement"></ns-region-selector>
 *   </template>
 * </demo-snippet>
 *
 */

const AUTO_REDIRECT_KEY = "autoRedirectGlobalLanding";
const CURRENT_LOCALE_KEY = "currentLocale";
const CURRENT_PAGE_KEY = "currentPage";
const REGIONS_ALLOWED_KEY = "regionsAllowed";
const HREF_OVERRIDE_KEY = "hrefOverride";

class NsRegionSelector extends PolymerElement {
  constructor() {
    super();
    let geoip2Variable = typeof geoip2 !== "undefined" ? geoip2 : null;
    this.geoIPMarketRedirect = new GeoIPMarketRedirectV2(geoip2Variable);
  }

  ready() {
    super.ready();
    this._autoDetectMarket();
  }

  static get is() {
    return "ns-region-selector";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Toggles the Warm Welcome animation sequence
       */
      active: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Prevents redirect and other features during authoring
       */
      authorMode: {
        type: Boolean,
        reflectToAttribute: true,
        value: false
      },
      /**
       * Detects the browser's country
       */
      detectedCountry: {
        type: String,
        value: "US"
      },
      /**
       * Detects the browser's language
       */
      detectedLanguage: {
        type: String,
        observer: "_getTranslation"
      },
      isKnown: {
        type: Boolean,
        reflectToAttribute: true
      },
      hideCheckbox: {
        type: Boolean,
        reflectToAttribute: true,
        value: false
      },
      /**
       * Sets which region is selected
       */
      navSelected: {
        type: Number,
        value: -1,
        reflectToAttribute: true
      },
      /**
       * Market region array
       */
      regions: {
        type: Array,
        value: []
      },
      /**
       * If set, this will filter out any language_country that is not in the array
       */
      regionsArray: {
        type: Array,
        value: []
      },
      /**
       * Translation object
       */
      translations: {
        type: Object,
        reflectToAttribute: true,
        value: {
          goToNuskin: [
            { text: "Go to ", languageCode: "en" },
            { text: "去", languageCode: "zh" },
            { text: "Vào trang ", languageCode: "vi" },
            { text: "IR A ", languageCode: "es" },
            { text: "ALLER AU", languageCode: "fr" },
            { text: "Pergi ke", languageCode: "my" },
            { text: "Masuk ke", languageCode: "in" },
            { text: "ไปที่ห", languageCode: "th" },
            { text: "ZU", languageCode: "de" },
            { text: "VAI A", languageCode: "it" },
            { text: "AVANÇAR PARA ", languageCode: "pt" },
            { text: "GA NAAR ", languageCode: "nl" },
            { text: "FARA Á", languageCode: "is" },
            { text: "GÅ TIL", languageCode: "da" },
            { text: "SIIRRY SIVULLE", languageCode: "fi" },
            { text: "GÅ TIL", languageCode: "no" },
            { text: "TILL", languageCode: "sv" },
            { text: "PRZEJDŹ DO ", languageCode: "pl" },
            { text: "ACCESAȚI", languageCode: "ro" },
            { text: "САЙТ", languageCode: "ru" },
            { text: "САЙТ", languageCode: "ua" }
          ],
          selectOtherRegionText: [
            { text: "その他のマーケットを選択", languageCode: "ja" },
            { text: "選擇其他地區", languageCode: "zh" },
            { text: "Chọn vùng khác", languageCode: "vi" },
            { text: "SELECCIONAR OTRA REGIÓN", languageCode: "es" },
            { text: "PILIH LOKASI YANG LAIN", languageCode: "my" },
            { text: "PILIH WILAYAH LAIN", languageCode: "in" },
            { text: "เลือกภูมิภาคอื่น", languageCode: "th" },
            { text: "ANDERE REGION AUSWÄHLEN", languageCode: "de" },
            { text: "SELEZIONA UN'ALTRA REGIONE", languageCode: "it" },
            { text: "SELECIONAR OUTRA REGIÃO", languageCode: "pt" },
            { text: "ANDERE REGIO KIEZEN", languageCode: "nl" },
            { text: "SÉLECTIONNER UNE AUTRE RÉGION", languageCode: "fr" },
            { text: "VELJA ANNAÐ SVÆÐI", languageCode: "is" },
            { text: "VÆLG EN ANDEN REGION", languageCode: "da" },
            { text: "VALITSE TOINEN MARKKINA-ALUE", languageCode: "fi" },
            { text: "VELG EN ANNEN REGION", languageCode: "no" },
            { text: "VÄLJ ANNAN REGION", languageCode: "sv" },
            { text: "VYBRAT JINÝ REGION", languageCode: "cs" },
            { text: "MÁSIK RÉGIÓ VÁLASZTÁSA", languageCode: "hu" },
            { text: "WYBIERZ INNY REGION", languageCode: "pl" },
            { text: "SELECTAȚI ALTĂ REGIUNE", languageCode: "ro" },
            { text: "VYBRAŤ INÝ REGIÓN", languageCode: "sk" },
            { text: "ВЫБРАТЬ ДРУГОЙ РЕГИОН", languageCode: "ru" },
            { text: "ВИБРАТИ ІНШИЙ РЕГІОН ", languageCode: "ua" },
            { text: "שמות המדינות", languageCode: "iw" }
          ],
          rememberMySelection: [
            { text: "記住我的選擇", languageCode: "zh" },
            { text: "選択内容を保存する", languageCode: "ja" },
            { text: "Ghi nhớ sự lựa chọn của tôi", languageCode: "vi" },
            { text: "Recordar mi selección", languageCode: "es" },
            { text: "Se Rappeler de ma Sèlection", languageCode: "fr" },
            { text: "INGATKAN PILIHAN SAYA", languageCode: "my" },
            { text: "INGAT PILIHAN SAYA", languageCode: "in" },
            { text: "จดจำข้อมูลที่ฉันเลือก", languageCode: "th" },
            { text: "Meine Auswahl speichern", languageCode: "de" },
            { text: "Ricorda la mia Selezione", languageCode: "it" },
            { text: "Recordar a minha selecão", languageCode: "pt" },
            { text: "Mijn keuze onthouden", languageCode: "nl" },
            { text: "MUNA HVAÐ ÉG VALDI", languageCode: "is" },
            { text: "Husk mit valg", languageCode: "da" },
            { text: "Muista valintani", languageCode: "fi" },
            { text: "Husk mitt valg ", languageCode: "no" },
            { text: "Kom ihåg mitt val", languageCode: "sv" },
            { text: "Zapamatovat si mou volbu", languageCode: "cs" },
            { text: "Választas megjegyzése", languageCode: "hu" },
            { text: "Pamiętaj mój wybór", languageCode: "pl" },
            { text: "Reţine selecţia mea", languageCode: "ro" },
            { text: "Zapamätať si voĺbu ", languageCode: "sk" },
            { text: "Запомнить мой выбор", languageCode: "ru" },
            { text: "Запам'ятати мій вибір", languageCode: "ua" },
            { text: "זכור את הבחירה שלי", languageCode: "iw" },
            { text: "EN filler", languageCode: "en" }
          ],
          countryTranslationOverrides: [],
          selectYourMarket: [
            { text: "選擇你的市場", languageCode: "zh" },
            { text: "マーケットを選択", languageCode: "ja" },
            { text: "filler", languageCode: "en" },
            { text: "fillador", languageCode: "es" }
          ],
          regionLabelTranslations: [],
          cookieDisclaimer: [],
          warmWelcome: [],
          warmWelcomeSubtitle: []
        }
      },
      translated: {
        type: Object,
        value: {
          goToNuskin: "Go to ",
          selectOtherRegionText: "Select Other Region",
          rememberMySelection: "Remember My Selection",
          selectYourMarket: "Select Your Market",
          cookieDisclaimer:
            "By clicking this checkbox you agree to set a cookie that will remember your location",
          warmWelcome: "Welcome!",
          warmWelcomeSubtitle: "Are you ready to discover the best you?"
        }
      },
      showTranslations: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      manualFlag: {
        type: String,
        value: "region-selection"
      },
      backgroundGif: {
        type: String,
        value:
          "/webcomponents/js/packages/elements-app/src/assets/map-swath.gif"
      },
      backgroundImage: {
        type: String,
        value:
          "/webcomponents/js/packages/elements-app/src/assets/map-swath.png"
      },
      checked: {
        type: Boolean,
        value: false,
        observer: "_toggleDisclaimer"
      },
      countryTranslationOverrides: Array,
      /**
       * If set, this will only show regions according to what is in the array
       * [ "en_US", "es_US" ]
       */
      regionsAllowed: {
        type: Array,
        value: []
      },
      /**
       * If set, this will override the page redirect
       */
      hrefOverride: {
        type: String,
        value: ""
      },
      /**
       * Cusom close event
       */
      onClose: {
        type: Function
      },
      /**
       * Custom Logo Link
       */
      logoLinkUrl: {
        type: String,
        value: "/"
      },
      /**
       * Custom handler for region(language) select
       */
      onRegionSelect: {
        type: Function
      }
    };
  }

  /**
   * Determines which market a user is in
   */
  _autoDetectMarket() {
    this.geoIPMarketRedirect
      .getMarketSiteRedirect()
      .then((data) => {
        this.isKnown = data.regionName !== "unknown" && !this.authorMode;
        if (data.countryCode) this.detectedCountry = data.countryCode;
        if (data.languageCode) this.detectedLanguage = data.languageCode;
        this._setRegions(data.regions, data.regionName);
      })
      .catch((data) => {
        this.isKnown = data.regionName !== "unknown" && !this.authorMode;
        if (data.countryCode) this.detectedCountry = data.countryCode;
        if (data.languageCode) this.detectedLanguage = data.languageCode;
        this._setRegions(data.regions, data.regionName);
        this._hideRememberMeCheckbox(data.countryCode, data.isEU);
        console.log("ns-region-selector autoDetectMarketException: ", data);
      });
  }

  /**
   * Closes the region selector and goes back
   */
  back() {
    if (this.onClose) {
      this.onClose();
      return;
    }
    window.location =
      SitesLocalStorageService.getItem(CURRENT_PAGE_KEY) ||
      "https://nuskin.com";
  }

  /**
   * Remove the current region selected from local storage
   */
  deleteCurrentSelection() {
    SitesLocalStorageService.removeItem(AUTO_REDIRECT_KEY);
  }

  /**
   * Removes active state from all tabs
   */
  _deselectTab() {
    this.navSelected = -1;
  }

  /**
   * Pulls the translations from the translation object's arrays
   */
  _getTranslation() {
    for (const key in this.translations) {
      this.translations[key].map((item, i) => {
        if (item.languageCode === this.detectedLanguage)
          this.set("translated." + key, this.translations[key][i].text);
      });
    }
    this.showTranslations = true;
  }

  /**
   * Toggles hidden the Remember My Selection checkbox
   */
  _hideRememberMeCheckbox(country, isEU) {
    this.hideCheckbox = isEU;
  }

  /**
   * Handles when a region is clicked
   */
  regionSelectOnClickListener(item) {
    let href = item.model.__data.item.href;
    if (href.includes("nuskinkorea") || href.includes("china.nuskin") || href.includes("nuskin.com.tw")) { 
      href = href.replace("/home.html", ""); 
    } 
    let locale = item.model.__data.item.locale || {};
    if (href) {
      this.checked
        ? this.saveCurrentSelection(href, locale)
        : this.deleteCurrentSelection();
      if (this.onRegionSelect) {
        this.onRegionSelect(item.model.__data.item);
        return;
      }
      window.location = href;
    }
  }

  /**
   * Saves the current locale and market selected to local storage
   */
  saveCurrentSelection(href, locale) {
    if (href.indexOf("nuskinkorea") === -1 && href.indexOf("china") === -1) {
      SitesLocalStorageService.writeItem(AUTO_REDIRECT_KEY, href);
      if (locale)
        SitesLocalStorageService.writeItem(CURRENT_LOCALE_KEY, locale);
    }
  }

  /**
   * Activates a tab's active state after a 800ms
   */
  _selectTab(e) {
    setTimeout(
      () => {
        this.navSelected = e.model.index;
      },
      this.active ? 0 : 800,
      this.setActive()
    );
  }

  /**
   * Activates the Warm Welcome animation sequence
   */
  setActive() {
    this.active = true;
  }

  /**
   * Finds the region translation based on cookie of region and language codes
   */
  _setRegions(data, regionName) {
    let currentLocale = SitesLocalStorageService.getItem(CURRENT_LOCALE_KEY);
    let regionsArr = [];
    let defaultLanguage = "en";

    if (currentLocale && currentLocale.language.length > 0)
      this.detectedLanguage = currentLocale.language;
    if (currentLocale && currentLocale.country.length > 0)
      this.detectedCountry = currentLocale.country;

    if (this.regionsAllowed.length <= 0) {
      this.set(
        "regionsAllowed",
        JSON.parse(SitesSessionStorageService.getItem(REGIONS_ALLOWED_KEY)) ||
          []
      );
      SitesSessionStorageService.removeItem(REGIONS_ALLOWED_KEY);
    }
    if (this.hrefOverride.length <= 0) {
      this.hrefOverride =
        SitesSessionStorageService.getItem(HREF_OVERRIDE_KEY) || "";
      SitesSessionStorageService.removeItem(HREF_OVERRIDE_KEY);
    }

    let allRegionsAllowed = this.regionsAllowed.length <= 0;

    data.map((region, i) => {
      let showRegion = false;
      let regionname = region.regionname ? region.regionname : "";

      if (regionname === currentLocale.region && allRegionsAllowed)
        this.navSelected = i;

      regionsArr.push({
        name: region.regionLabel[this.detectedLanguage]
          ? region.regionLabel[this.detectedLanguage]
          : region.regionLabel.en,
        countryname: [],
        regionname: regionname
      });

      region.countryname.map((countryname, j) => {
        let showCountry = false;
        let name = "";

        if (
          this.countryTranslationOverrides &&
          this.countryTranslationOverrides.length > 0
        ) {
          this.countryTranslationOverrides.map((translationOverride) => {
            if (
              translationOverride.countryCode === countryname.countryCode &&
              translationOverride.languageCode === this.detectedLanguage
            )
              name = translationOverride.translatedValue;
          });
        }
        if (
          countryname.countryCode === this.detectedCountry &&
          allRegionsAllowed
        ) {
          this.navSelected = i;
          this.isKnown = true;
        }

        regionsArr[i].countryname.push({
          name:
            name.length > 0
              ? name
              : countryname.locale_names.hasOwnProperty(this.detectedLanguage)
                ? countryname.locale_names[this.detectedLanguage]
                : countryname.locale_names[defaultLanguage],
          href: allRegionsAllowed
            ? countryname.paths[countryname.defaultLocale.defaultLanguageCode]
            : "javascript:void(0)",
          languages: [],
          active:
            this.detectedCountry === countryname.countryCode &&
            regionName !== "unknown"
              ? "active"
              : "",
          locale: {
            region: regionname,
            language: countryname.defaultLocale.defaultLanguageCode,
            country: countryname.countryCode
          }
        });

        countryname.languageCodes.map((languageCode) => {
          let showLanguage = false;

          if (!allRegionsAllowed) {
            showLanguage = this.regionsAllowed.includes(
              languageCode + "_" + countryname.countryCode
            );
            if (showLanguage) showRegion = showCountry = true;
          } else {
            showLanguage = showRegion = showCountry = true;
          }

          let languageHref = countryname.paths[languageCode];

          if (this.hrefOverride.length > 0 && languageHref) {
            let lastIndex = languageHref.lastIndexOf(
              languageCode + "_" + countryname.countryCode + "/"
            );
            if (
              this.hrefOverride[0] === "/" &&
              languageHref[languageHref.length - 1] === "/"
            ) {
              languageHref = languageHref.substring(0, languageHref.length - 1);
            }
            let cleanHrefOverride = this.hrefOverride.split(/\/\w{2}_\w{2}\//);
            let override = cleanHrefOverride[cleanHrefOverride.length - 1];
            this.hrefOverride = override.startsWith("/")
              ? override
              : "/" + override;
            languageHref =
              lastIndex > -1
                ? languageHref.substring(lastIndex + 5, -1) + this.hrefOverride
                : languageHref + this.hrefOverride;
          }

          regionsArr[i].countryname[j].languages.push({
            name: countryname.displayLanguage[languageCode],
            href: languageHref,
            active:
              this.detectedLanguage === languageCode && regionName !== "unknown"
                ? "active"
                : "",
            locale: {
              region: regionname,
              language: languageCode,
              country: countryname.countryCode
            },
            show: showLanguage
          });
        });
        regionsArr[i].countryname[j].show = showCountry;
      });
      regionsArr[i].show = showRegion;
    });

    this.set("regions", regionsArr);

    if (this.navSelected > -1) this.setActive();
    if (window.innerWidth < 1024) {
      let t = this.isKnown ? 0 : 3000;
      setTimeout(() => {
        this.toggleMobile();
      }, t);
    }
  }

  /**
   * Filters regions / countries / and languages not in regionsAllowed
   */
  filterLocations(item) {
    return item.show;
  }

  /**
   * Sorts the countries in the regions object
   */
  sortCountriesAlphabetically(a, b) {
    if (b.locale.country === "CN") {
      return 1;
    } else if (a.locale.country === "CN") {
      return -1;
    }

    if (b.locale.country === "TW") {
      return 1;
    } else if (a.locale.country === "TW") {
      return -1;
    }

    if (b.locale.country === "HK") {
      return 1;
    } else if (a.locale.country === "HK") {
      return -1;
    }

    return a.name.toLowerCase() < b.name.toLowerCase()
      ? -1
      : a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : 0;
  }

  /**
   * Hide and show cookie disclaimer
   */
  _toggleDisclaimer() {
    this.checked ? this.$.disclaimer.show() : this.$.disclaimer.hide();
  }

  /**
   * Toggles the display open for the mobile animation sequence
   */
  toggleMobile() {
    this.$.mobileCollapse.toggle();
    this.setActive();
  }
}
customElements.define(NsRegionSelector.is, NsRegionSelector);
